/**
 * TasksRelatedTabs - Subabas por tipo de tarefa (Normal | Retenção | Jurídico)
 *
 * Envolve a TasksRelatedList em subabas, uma por task_type. Cada subaba monta a lista
 * dentro de um <TaskModuleProvider>, o que faz a RPC get_tasks_paginated ser chamada
 * com o p_task_type correspondente (sem isso a RPC assume 'normal' e as tarefas de
 * Retenção/Jurídico ficam invisíveis nos modais de Cliente/Negócio/Veículo).
 *
 * As subabas de Retenção e Jurídico só aparecem quando os submódulos estão habilitados
 * no tenant (useHeaderModuleFlags — fonte única de verdade).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-21 v1.1.0 - Exibe contador (badge) de tarefas em cada subaba, via
 *          useRelatedTasksTypeCounts (uma única chamada para os 3 tipos).
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial
 */
import { useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TaskModuleProvider } from '@/contexts/TaskModuleContext';
import { TasksRelatedList } from './TasksRelatedList';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { useRelatedTasksTypeCounts } from '@/hooks/useRelatedTasksTypeCounts';
import { cn } from '@/lib/utils';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

interface TasksRelatedTabsProps {
  /** Mesmo contrato da TasksRelatedList */
  context: { dealId?: string; contactId?: string | null; vehicleId?: string | null };
  vehicleLabel?: string;
  onNavigateToTask?: (taskId: string) => void;
  /** Header compacto (uso no sidebar do chat) */
  compactHeader?: boolean;
}

export function TasksRelatedTabs({ context, vehicleLabel, onNavigateToTask, compactHeader = false }: TasksRelatedTabsProps) {
  const { isRetentionAvailable, isLegalAvailable } = useHeaderModuleFlags();
  const [active, setActive] = useState<TaskModuleType>('normal');
  /** v1.1.0: contadores por tipo, exibidos como badge em cada subaba */
  const { data: counts } = useRelatedTasksTypeCounts(context);

  /** Abas visíveis conforme flags do tenant */
  const tabs = useMemo(() => {
    const list: Array<{ value: TaskModuleType; label: string }> = [{ value: 'normal', label: 'Normal' }];
    if (isRetentionAvailable) list.push({ value: 'retention', label: 'Retenção' });
    if (isLegalAvailable) list.push({ value: 'legal', label: 'Jurídico' });
    return list;
  }, [isRetentionAvailable, isLegalAvailable]);

  // Apenas o tipo normal habilitado → sem subabas (mantém o layout atual)
  if (tabs.length === 1) {
    return (
      <TaskModuleProvider type="normal">
        <TasksRelatedList
          context={context}
          vehicleLabel={vehicleLabel}
          onNavigateToTask={onNavigateToTask}
          compactHeader={compactHeader}
        />
      </TaskModuleProvider>
    );
  }

  return (
    <Tabs value={active} onValueChange={(v) => setActive(v as TaskModuleType)} className="w-full">
      <TabsList className="h-8 mb-3">
        {tabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value} className="text-xs px-3 h-7">
            {t.label}
            {/* Badge de contagem: some quando não há tarefas do tipo */}
            {(counts?.[t.value] ?? 0) > 0 && (
              <span
                className={cn(
                  'ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 h-4 min-w-4',
                  'text-[10px] font-medium bg-muted text-muted-foreground',
                  active === t.value && 'bg-primary/10 text-primary',
                )}
              >
                {counts?.[t.value]}
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((t) => (
        <TabsContent key={t.value} value={t.value} className="mt-0">
          {/* Monta a lista somente da aba ativa: evita 3 chamadas simultâneas à RPC */}
          {active === t.value && (
            <TaskModuleProvider type={t.value}>
              <TasksRelatedList
                context={context}
                vehicleLabel={vehicleLabel}
                onNavigateToTask={onNavigateToTask}
                compactHeader={compactHeader}
              />
            </TaskModuleProvider>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
}
